import {
    ChangeDetectionStrategy,
    Component,
    ElementRef,
    Injector,
    Input,
    OnChanges,
    OnInit,
    output,
    SimpleChange,
    SimpleChanges,
    ViewEncapsulation,
    inject,
    effect,
    signal,
    model,
    input,
    computed,
    ViewChild,
} from '@angular/core';
import { isDefined } from '@fi-shared/utils';
import { NG_VALIDATORS, NG_VALUE_ACCESSOR, ValidationErrors } from '@angular/forms';
import { ControlValueAccessorValidatorBase } from '@fi-shared/control-value-accessor-validator-base';
import {
    FiTimePickerHelperService,
    TimePickerSegmentIndex,
} from '@fi-shared/fi-time-picker-input/fi-time-picker-helper.service';
import { isNullOrUndefined } from '@fi-shared/utils/is-null-or-undefined';
import { FiFormErrorDisplayContextService } from '@fi-shared/fi-shared-services/fi-form-error-display-context.service';
import {
    ResolvedFiTimePickerInputConfig,
    FiTimePickerInputConfig,
    ITimeSegment,
    ITimePickerTimeLimits,
    ITimeSegmentInputData,
} from './fi-time-picker-input.interfaces';
import { FiTimePickerSegmentInputComponent } from './fi-time-picker-segment-input/fi-time-picker-segment-input.component';
import { guid } from '../utils/guid';

const defaultTimePickerConfig: ResolvedFiTimePickerInputConfig = {
    showSecondaryTimeInput: false,
    showSeconds: false,
    minutesGap: 5,
    secondsGap: 5,
    maxHours: 24,
    maxMinutes: 60,
    maxSeconds: 60,
    mainInputLabelId: 'common.start_time',
    secondaryInputLabelId: 'common.end_time',
};

@Component({
    selector: 'fi-time-picker-input',
    templateUrl: './fi-time-picker-input.component.html',
    styleUrl: './fi-time-picker-input.component.scss',
    encapsulation: ViewEncapsulation.None,
    changeDetection: ChangeDetectionStrategy.OnPush,
    host: { class: 'fi-time-picker-input' },
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            multi: true,
            useExisting: FiTimePickerInputComponent,
        },
        {
            provide: NG_VALIDATORS,
            multi: true,
            useExisting: FiTimePickerInputComponent,
        },
    ],
    standalone: false,
})
export class FiTimePickerInputComponent extends ControlValueAccessorValidatorBase<string> implements OnInit, OnChanges {
    @Input() secondaryTimeValue: string = '';
    public secondaryTimeValueChange = output<string>();
    public triggerInputIdChange = input();

    @Input() isRequired: boolean = true;
    @Input() disabled: boolean = false;

    public minMainTime = input<string>('');
    public maxMainTime = input<string>('');

    public minSecondaryTime = input<string>('');
    public maxSecondaryTime = input<string>('');

    public nextDayHoursCount = input<number>(0);
    public isNextDay = model<boolean>(false);
    public isNextDayCapable = input<boolean>(false);

    @ViewChild('mainSegmentInput') _mainSegmentInput: FiTimePickerSegmentInputComponent;
    private _secondarySegmentInput: FiTimePickerSegmentInputComponent | undefined = undefined;
    @ViewChild('secondarySegmentInput') set secondarySegmentInput(value: FiTimePickerSegmentInputComponent) {
        if (isDefined(value)) {
            this._secondarySegmentInput = value;
        }
    }

    public timepickerInputConfig = input<FiTimePickerInputConfig>();
    protected readonly config = computed<ResolvedFiTimePickerInputConfig>(() => ({
        ...defaultTimePickerConfig,
        ...(this.timepickerInputConfig() ?? {}),
    }));

    public isHighlighted = model<boolean>();

    private fiTimePickerHelperService = inject(FiTimePickerHelperService);
    private readonly fiFormErrorDisplayContextService = inject(FiFormErrorDisplayContextService, { optional: true });

    protected isErrorShown = signal(false);
    protected mainSegmentInputValue = signal<ITimeSegmentInputData>(undefined);
    protected secondarySegmentInputValue = signal<ITimeSegmentInputData>(undefined);

    private inputHighlightTimer: ReturnType<typeof setTimeout>;
    private changesValidationTimer: ReturnType<typeof setTimeout>;
    private readonly changesValidationTimeout: number = 500;

    protected showSeconds = computed(() => this.config().showSeconds);

    private readonly inputIdSuffix = guid();
    protected mainTimeInputId: string = 'main-time-input-' + this.inputIdSuffix;
    protected secondaryTimeInputId: string = 'secondary-time-input-' + this.inputIdSuffix;

    protected minutes = computed<ITimeSegment[]>(() => {
        const minutesGap = this.config().minutesGap;
        const maxMinutes = this.config().maxMinutes;

        return this.fiTimePickerHelperService
            .getTimeSegment(Math.round(maxMinutes / minutesGap), (index: number) => index * minutesGap)
            .map((value: string, index: number) => {
                return {
                    value,
                    id: index,
                };
            });
    });
    protected seconds = computed<ITimeSegment[]>(() => {
        if (!this.config().showSeconds) {
            return [];
        }
        const secondsGap = this.config().secondsGap;
        const maxSeconds = this.config().maxSeconds;

        return this.fiTimePickerHelperService
            .getTimeSegment(Math.round(maxSeconds / secondsGap), (index: number) => index * secondsGap)
            .map((value: string, index: number) => {
                return {
                    value,
                    id: index,
                };
            });
    });

    protected mainSegments = computed(() => ({
        hours: this.getHoursForInput(true),
        minutes: this.minutes(),
        seconds: this.seconds(),
    }));

    protected secondarySegments = computed(() => ({
        hours: this.getHoursForInput(false),
        minutes: this.minutes(),
        seconds: this.seconds(),
    }));

    protected mainTimeLimits = signal<ITimePickerTimeLimits>(undefined);
    protected secondaryTimeLimits = signal<ITimePickerTimeLimits>(undefined);

    constructor(injector: Injector) {
        super(injector);

        effect(() => {
            this.fiFormErrorDisplayContextService?.showValidationErrors();
            this.validateInputs();
        });

        effect(() => {
            const isHighlighted = this.isHighlighted();

            if (isDefined(this.inputHighlightTimer)) {
                clearTimeout(this.inputHighlightTimer);
            }

            this.inputHighlightTimer = setTimeout(() => {
                this.isHighlighted.set(false);
                this.cdr.markForCheck();
            }, this.fiTimePickerHelperService.inputHighlightTimeout);
        });

        effect(() => {
            const config = this.config();
            const nextDayHoursCount = this.nextDayHoursCount();
            this.mainTimeLimits.set(this.getComparableTimeLimits(true));
            this.secondaryTimeLimits.set(this.getComparableTimeLimits(false));
        });

        effect(() => {
            const minMainTime = this.minMainTime();
            const maxMainTime = this.maxMainTime();
            const minSecondaryTime = this.minSecondaryTime();
            const maxSecondaryTime = this.maxSecondaryTime();

            this.mainTimeLimits.set(this.getComparableTimeLimits(true));
            this.secondaryTimeLimits.set(this.getComparableTimeLimits(false));
        });

    }

    get mainTimeValue(): string {
        // hh:mm or hh:mm:ss;
        return this.ngModel;
    }

    public ngOnInit(): void {
        this.handleIncorrectModelFormat();
        // this.setInputsUniqueIds();
        this.setTimeSegmentInputsValues();
    }

    public ngOnChanges(changes: SimpleChanges): void {
        if (changes.ngModel?.currentValue?.length) {
            this.ngModel = this.fiTimePickerHelperService.handleNaNTimeSegment(changes.ngModel.currentValue);
            this.setTimeSegmentInputsValues();
        }

        if (changes.secondaryTimeValue?.currentValue?.length) {
            this.secondaryTimeValue = this.fiTimePickerHelperService.handleNaNTimeSegment(
                changes.secondaryTimeValue.currentValue,
            );
            this.setTimeSegmentInputsValues();
        }

        if (changes.ngModel || changes.secondaryTimeValue) {
            this.validateInputs();
        }

        const disabledChange: SimpleChange = changes.disabled;
        if (isDefined(disabledChange) && !disabledChange.firstChange) {
            this.validateInputs();
        }
    }

    private setTimeSegmentInputsValues(): void {
        this.mainSegmentInputValue.set(
            this.fiTimePickerHelperService.convertStringToTimeSegmentInputData(
                this.ngModel,
                this.mainSegments(),
                false,
            ),
        );

        if (this.config().showSecondaryTimeInput) {
            this.secondarySegmentInputValue.set(
                this.fiTimePickerHelperService.convertStringToTimeSegmentInputData(
                    this.secondaryTimeValue,
                    this.secondarySegments(),
                    this.isNextDay(),
                ),
            );
        }
    }

    protected resetTimeValue(): void {
        this.ngModel = undefined;
        this.doPropagateChanges();
        this.secondaryTimeValue = undefined;
        this.isNextDay.set(false);
        this.mainSegmentInputValue.set({
            hours: { id: undefined, value: undefined },
            minutes: { id: undefined, value: undefined },
            seconds: { id: undefined, value: undefined },
        });
        this.secondarySegmentInputValue.set({
            hours: { id: undefined, value: undefined },
            minutes: { id: undefined, value: undefined },
            seconds: { id: undefined, value: undefined },
        });
    }

    private validateInputs(): void {
        const errors: ValidationErrors = this.doValidate();
        const invalid = Object.values(errors)?.some((isError: boolean) => isError);
        const showValidationErrors = this.fiFormErrorDisplayContextService?.showValidationErrors() ?? true;
        this.isErrorShown.set(invalid && showValidationErrors);
    }

    private isTimeValueInvalid(timeValue: string, isMainTime: boolean): boolean {
        return (
            this.fiTimePickerHelperService.isTimeValueInvalid({
                timeValue,
                currentTimeValue: isMainTime ? this.mainTimeValue : this.secondaryTimeValue,
                showSeconds: this.config().showSeconds,
            }) || this.isSelectedTimeDisabled(isMainTime)
        );
    }

    private isSelectedTimeDisabled(isMainTime: boolean): boolean {
        return isMainTime
            ? this._mainSegmentInput?.isSelectedTimeSegmentInvalid()
            : this._secondarySegmentInput?.isSelectedTimeSegmentInvalid();
    }

    protected isTimeSegmentDisabled(
        timeSegment: ITimeSegment,
        segmentIndex: TimePickerSegmentIndex,
        isMainTime: boolean,
    ): boolean {
        const timeLimits = isMainTime ? this.mainTimeLimits() : this.secondaryTimeLimits();

        return this.fiTimePickerHelperService.isTimeSegmentDisabled(
            timeLimits,
            segmentIndex,
            timeSegment,
            isMainTime ? this.mainSegmentInputValue() : this.secondarySegmentInputValue(),
        );
    }

    private getHoursForInput(isMainInput: boolean): ITimeSegment[] {
        const maxHours = this.config().maxHours;

        const sameDayHours: ITimeSegment[] = this.fiTimePickerHelperService
            .getTimeSegment(maxHours)
            .map((value: string, index: number) => ({
                value,
                id: index,
            }));

        if (isMainInput) {
            return sameDayHours;
        }

        const overtimeHours: ITimeSegment[] = this.fiTimePickerHelperService
            .getTimeSegment(this.nextDayHoursCount())
            .map((value: string, index: number) => ({
                value,
                id: maxHours + index,
            }));

        return [...sameDayHours, ...overtimeHours];
    }

    protected onMainValueChange(): void {
        this.secondaryTimeLimits.set(this.getComparableTimeLimits(false));
        this.ngModel = this.fiTimePickerHelperService.convertTimeSegmentInputDataToString(
            this.mainSegmentInputValue(),
            this.config().showSeconds,
        );
        this.doPropagateChanges();
    }

    protected onSecondaryValueChange(): void {
        this.mainTimeLimits.set(this.getComparableTimeLimits(true));
        this.secondaryTimeValueChange.emit(
            this.fiTimePickerHelperService.convertTimeSegmentInputDataToString(
                this.secondarySegmentInputValue(),
                this.config().showSeconds,
            ),
        );

    }

    private getComparableTimeLimits(isMainInput: boolean): ITimePickerTimeLimits {
        const timeLimits: ITimePickerTimeLimits = this.fiTimePickerHelperService.getCurrentTimeLimits({
            isMainInput,
            minMainTime: this.minMainTime(),
            maxMainTime: this.maxMainTime(),
            minSecondaryTime: this.minSecondaryTime(),
            maxSecondaryTime: this.maxSecondaryTime(),
        });

        if (isMainInput || !this.nextDayHoursCount()) {
            return timeLimits;
        }

        const maxHours = this.config().maxHours;
        const overtimeMaxHour = this.nextDayHoursCount() - 1;

        return {
            ...timeLimits,
            maxHour:
                isDefined(timeLimits.maxHour) && timeLimits.maxHour <= overtimeMaxHour
                    ? timeLimits.maxHour + maxHours
                    : timeLimits.maxHour,
        };
    }

    private handleIncorrectModelFormat(): void {
        if (
            !!this.ngModel?.length &&
            this.ngModel.split(':').length <
                this.fiTimePickerHelperService.getMinTimeSegments(this.config().showSeconds)
        ) {
            throw new Error('Wrong ngModel format in FiTimePickerInputComponent. Expected hh:mm or hh:mm:ss');
        }
    }

    protected handleEnterPressMainInput($event: Event): void {
        $event.preventDefault();
        this.doPropagateChanges();

        const secondaryInput: HTMLInputElement = this.getDocumentInputElById(this.secondaryTimeInputId);
        if (!isNullOrUndefined(secondaryInput)) {
            secondaryInput.focus();
        }
    }

    protected handleEnterPressSecondaryInput($event: Event): void {
        $event.preventDefault();
        this.doPropagateChanges();
        this.getDocumentInputElById(this.secondaryTimeInputId)?.blur();
    }

    private getDocumentInputElById(id: string): HTMLInputElement {
        return document.getElementById(id) as HTMLInputElement;
    }

    public doPropagateChanges(): void {
        if (isDefined(this.changesValidationTimer)) {
            clearTimeout(this.changesValidationTimer);
        }

        this.changesValidationTimer = setTimeout(() => {
            this.validateInputs();
            super.doPropagateChanges();
            this.secondaryTimeValueChange.emit(this.secondaryTimeValue);
        }, this.changesValidationTimeout);
    }

    public doValidate(): ValidationErrors {
        if (this.disabled) {
            return {};
        }

        return {
            required: this.isInputNotFilled(),
            'invalid-input': this.isInputInvalid(),
        };
    }

    private isInputNotFilled(): boolean {
        return (
            this.isRequired &&
            (!this.mainTimeValue?.length || (this.config().showSecondaryTimeInput && !this.secondaryTimeValue?.length))
        );
    }

    private isInputInvalid(): boolean {
        return (
            this.isTimeValueInvalid(this.mainTimeValue, true) ||
            (this.config().showSecondaryTimeInput && this.isTimeValueInvalid(this.secondaryTimeValue, false))
        );
    }

    protected readonly TimePickerSegmentIndex = TimePickerSegmentIndex;
}
